// Copyright (c) Docugami, Inc. All rights reserved.

import axios from "axios";
import { Config } from "config";
import dayjs from "dayjs";
import { ProviderId, StorageProviderAuthenticationResponse, StorageProviderTokenRequest, UploadDocumentsStatus, UploadDocumentsRequest, UploadStatus } from "networking/StorageProviders";
import { IObservableCollection, ObservableArray } from "utils/ObservableArray";
import { RefCount } from "utils/RefCount";
import { RetryConfiguration, RetryResult } from "utils/RetryPromise";
import { StorageKey } from "utils/StorageAccessor";
import { TrackedRequest, TrackedRequests } from "utils/TrackedRequests";
import { Event, IEvent } from "utils/Event";
import { Token, TokenResponse } from "networking/Authentication";
const { v4: uuidv4 } = require('uuid');

export interface FileUploadCounts {
    totalCount: number;
    failedCount: number;
    succeededCount: number;
}

export interface IStorageManager {
    // Authentication
    requestToken: (providerId: ProviderId, oauthProviderId?: string) => Promise<TokenResponse>;
    authenticate: (providerId: ProviderId, requestId: string, oauthProviderId?: string) => Promise<StorageProviderAuthenticationResponse>;

    // Uploads
    uploadRequest: (request: UploadDocumentsRequest) => Promise<string>;
    setOrgId: (orgId: string) => void;
    enableUploadStatus(): () => void;
    activeUploads: IObservableCollection<UploadDocumentsStatus>;
    onFileUpload: IEvent<FileUploadCounts>;
}

class StorageManager implements IStorageManager {
    private readonly _tokenCache: Map<ProviderId, Token> = new Map();
    private readonly _oauthTokenCache: Map<string, Token> = new Map();

    // Upload status tracking
    private _uploadRequests!: TrackedRequests<UploadDocumentsStatus>;
    private readonly _fileUploadEvent: Event<FileUploadCounts> = new Event();
    private readonly _shouldPollUploadStatusResultCallback: (result: UploadDocumentsStatus) => boolean;
    private readonly _shouldPollUploadStatusErrorCallback: (error: any) => boolean;
    private readonly _onUploadStatusResultCallback: (request: TrackedRequest, result: RetryResult<UploadDocumentsStatus>) => void;
    private _activeUploads: ObservableArray<UploadDocumentsStatus> = new ObservableArray<UploadDocumentsStatus>([], (item1, item2) => item1.requestId === item2.requestId);
    private _uploadReferences!: RefCount;
    private _orgId: string = '';

    get activeUploads(): IObservableCollection<UploadDocumentsStatus> {
        return this._activeUploads;
    }

    get onFileUpload(): IEvent<FileUploadCounts> { return this._fileUploadEvent; }

    constructor() {
        this._shouldPollUploadStatusResultCallback = this.shouldPollUploadStatusResult.bind(this);
        this._shouldPollUploadStatusErrorCallback = this.shouldPollUploadStatusError.bind(this);
        this._onUploadStatusResultCallback = this.onUploadStatusResult.bind(this);
        this.initializeUploadRequests();
    }

    setOrgId(orgId: string): void {
        if (!orgId) {
            throw new Error('Invalid argument: orgId should be nonempty string.')
        }

        // only update the orgId if it has changed
        if (orgId === this._orgId) {
            return;
        }

        this._orgId = orgId;
        this.initializeUploadRequests();
    }

    enableUploadStatus(): () => void {
        return this._uploadReferences.acquire();
    }

    async requestToken(providerId: ProviderId, oauthProviderId?: string): Promise<TokenResponse> {
        if (providerId === ProviderId.OAuth) {
            if (this._oauthTokenCache.has(oauthProviderId!)) {
                const cachedToken = this._oauthTokenCache.get(oauthProviderId!)!;
                if (!isTokenExpired(cachedToken, 180)) {
                    return {
                        accessToken: cachedToken
                    }
                }
            }
        }

        if (this._tokenCache.has(providerId)) {
            const cachedToken = this._tokenCache.get(providerId)!;
            if (!isTokenExpired(cachedToken, 180)) {
                return {
                    accessToken: cachedToken
                }
            }
        }

        // Perform the request
        const input: StorageProviderTokenRequest = {
            providerId: providerId,
            oauthProviderId: oauthProviderId
        };

        return await this.performTokenRequest(input);
    }

    async authenticate(providerId: ProviderId, requestId: string, oauthProviderId?: string): Promise<StorageProviderAuthenticationResponse> {
        const url = `${Config.storageUrl}/connections/auth/status/${encodeURIComponent(requestId)}`
        const authResponse = await axios.get<StorageProviderAuthenticationResponse>(url);

        if (!!authResponse.data.accessToken) {
            if (providerId === ProviderId.OAuth) {
                this._oauthTokenCache.set(ProviderId.OAuth, authResponse.data.accessToken);
            }
            else {
                this._tokenCache.set(providerId, authResponse.data.accessToken);
            }
        }

        return authResponse.data;
    }

    async uploadRequest(request: UploadDocumentsRequest): Promise<string> {
        if (request.items.length === 0) {
            throw new Error("Cannot upload an empty item set.");
        }

        // Optimistic UX updates
        const tempStatus: UploadDocumentsStatus = {
            requestId: uuidv4(),
            providerId: request.providerId,
            status: UploadStatus.GatheringFiles,
            timeStamp: new Date(),
            totalCount: 0,
            failedCount: 0,
            succeededCount: 0
        }

        const url = `${Config.importApiUrl}/${encodeURIComponent(request.providerId)}/upload`

        let requestId: string;
        try {
            const response = await axios.post<string>(url, request);
            requestId = response.data;
            tempStatus.requestId = requestId;
        }
        catch (error: any) {
            tempStatus.status = UploadStatus.Failed;
            throw error;
        }

        this.handleUploadStatusResult(tempStatus);

        this._uploadRequests.track({
            id: requestId,
            url: `${Config.importApiUrl}/${encodeURIComponent(request.providerId)}/upload/${encodeURIComponent(requestId)}`,
            timestamp: new Date()
        });

        return requestId;
    }

    private initializeUploadRequests() {
        const config: RetryConfiguration<UploadDocumentsStatus> = {
            intervalMs: 2000,
            maxRetryCount: 300, // 10 minutes
            resultHandler: this._shouldPollUploadStatusResultCallback,
            errorHandler: this._shouldPollUploadStatusErrorCallback
        };

        this._uploadRequests = new TrackedRequests<UploadDocumentsStatus>(StorageKey.StorageProviderUploads, config, this._orgId);
        this._uploadReferences = new RefCount(
            () => this._uploadRequests.start(this._onUploadStatusResultCallback),
            () => {
                this._uploadRequests.stop();
                this._activeUploads.reset();
            });
    }

    private async performTokenRequest(input: StorageProviderTokenRequest): Promise<TokenResponse> {
        if (input.providerId === ProviderId.OAuth) {
            this._oauthTokenCache.delete(input.oauthProviderId!);
        } else {
            this._tokenCache.delete(input.providerId);
        }

        // Make the initial request to get the token or initiate the authentication flow
        const url = `${Config.storageUrl}/connections/auth/token`;
        const response = await axios.post<TokenResponse>(url, input);

        if (!!response.data.accessToken) {
            if (input.providerId === ProviderId.OAuth) {
                this._oauthTokenCache.set(input.oauthProviderId!, response.data.accessToken);
            } else {
                this._tokenCache.set(input.providerId, response.data.accessToken);
            }
        }

        return response.data;
    }

    private shouldPollUploadStatusResult(status: UploadDocumentsStatus): boolean {
        const isComplete = status.status === UploadStatus.Complete || status.status === UploadStatus.Failed;
        return !isComplete;
    }

    private shouldPollUploadStatusError(error: any): boolean {
        // Stop polling on failures
        return false;
    }

    private onUploadStatusResult(request: TrackedRequest, result: RetryResult<UploadDocumentsStatus>) {
        if (!!result.result) {
            this.handleUploadStatusResult(result.result);
        }
        else if (result.isComplete) {
            this._activeUploads.removeByKeyValue("requestId", request.id);
        }
    }

    private handleUploadStatusResult(status: UploadDocumentsStatus) {
        const isComplete = status.status === UploadStatus.Complete || status.status === UploadStatus.Failed;

        const event = {
            totalCount: status.totalCount,
            failedCount: status.failedCount,
            succeededCount: status.succeededCount
        };

        if (this._activeUploads.items.find(item => item.requestId === status.requestId &&
            item.status === UploadStatus.GatheringFiles &&
            status.status === UploadStatus.Uploading)) {
            this._fileUploadEvent.raise(event);
        }

        this._activeUploads.addOrUpdate(status);

        if (isComplete) {
            this._activeUploads.remove(status);
            this._fileUploadEvent.raise(event);
        }
    }
}

export function isTokenExpired(token: Token, validityWindowS: number): boolean {
    const tokenDate = dayjs(new Date(token.createdAt));
    const expirationDate = tokenDate.add(token.expirationSeconds, 'seconds');
    const checkDate = dayjs(new Date()).add(validityWindowS, 'seconds');

    return checkDate >= expirationDate;
}

export const StorageManagerInstance: IStorageManager = new StorageManager();
